import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Delete, Loader2, Briefcase } from "lucide-react";
import { useOwnerSession, getRememberedCompanyCode } from "@/hooks/useOwnerSession";
import { isRegularOwnerSession } from "@/lib/ownerSessionRouting";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { LegalFooter } from "@/components/LegalFooter";

/**
 * Unternehmer-Master-PIN-Login (Sprint A.5, Feb 2026)
 *
 * 6-stelliger PIN. Schwester-Komponente zu `PinLogin.jsx` (4 Stellen, Mitarbeiter).
 *
 * Designentscheidungen:
 *  - Helles Layout im Gegensatz zum dunklen Mitarbeiter-Login → schon optisch
 *    klar getrennt.
 *  - Auto-Submit bei 6 Ziffern.
 *  - Default-PIN-Hinweis nur, wenn ``/auth/owner/has-default-pin`` true liefert
 *    (= owner_pin_changed_at noch nicht gesetzt). Andernfalls würden wir die
 *    halbe Antwort an alle Besucher leaken.
 */
export default function OwnerLogin() {
  const navigate = useNavigate();
  const { session, login, loading } = useOwnerSession();
  const [pin, setPin] = useState("");
  const [companyCode, setCompanyCode] = useState(getRememberedCompanyCode);
  const [submitting, setSubmitting] = useState(false);
  const [shake, setShake] = useState(false);
  const [hasDefaultPin, setHasDefaultPin] = useState(false);

  useEffect(() => {
    if (loading) return;
    // V2I: public-preview löst hier keinen automatischen Redirect aus.
    if (isRegularOwnerSession(session)) {
      navigate("/unternehmer", { replace: true });
    }
  }, [session, loading, navigate]);

  // IDLE-AUTO-LOGOUT (Feb 2026): wenn die Login-Seite mit ?reason=idle
  // erreicht wird (401-Interceptor hat wegen abgelaufener Session
  // weitergeleitet), klarer Hinweis-Toast + URL aufräumen, damit ein
  // späteres Reload nicht erneut den Toast zeigt.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("reason") === "idle") {
      toast.info(
        "Ihre Sitzung ist wegen Inaktivität abgelaufen. Bitte neu anmelden.",
        { duration: 6000 },
      );
      params.delete("reason");
      const query = params.toString();
      const cleanUrl =
        window.location.pathname + (query ? `?${query}` : "") + window.location.hash;
      window.history.replaceState({}, "", cleanUrl);
    }
  }, []);

  useEffect(() => {
    // P1.1-Härtung: Default-PIN-Hinweis NUR firmenbezogen abfragen (tenant-
    // scoped). Ohne eingegebenen Firmen-Code wird nichts geprüft.
    const code = companyCode.trim();
    if (!code) {
      setHasDefaultPin(false);
      return;
    }
    let cancelled = false;
    const t = setTimeout(() => {
      api
        .hasDefaultOwnerPin(code)
        .then((res) => {
          if (!cancelled) setHasDefaultPin(!!res?.has_default_pin);
        })
        .catch(() => {
          if (!cancelled) setHasDefaultPin(false);
        });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [companyCode]);

  const submitPin = (full) => {
    if (submitting) return;
    const code = companyCode.trim();
    if (!code) {
      toast.error("Bitte zuerst den Firmen-Code eingeben.");
      setPin("");
      return;
    }
    setSubmitting(true);
    login(code, full)
      .then(() => navigate("/unternehmer", { replace: true }))
      .catch((err) => {
        const detail = err?.response?.data?.detail || "Firma oder PIN unbekannt.";
        toast.error(detail);
        setShake(true);
        setTimeout(() => setShake(false), 400);
        setPin("");
        setSubmitting(false);
      });
  };

  const append = (d) => {
    if (submitting) return;
    setPin((p) => {
      if (p.length >= 6) return p;
      const next = p + d;
      if (next.length === 6) submitPin(next);
      return next;
    });
  };
  const backspace = () => {
    if (submitting) return;
    setPin((p) => p.slice(0, -1));
  };

  return (
    <div
      className="min-h-screen bg-slate-50 flex flex-col"
      data-testid="owner-login-page"
    >
      <header className="px-5 py-4 flex items-center">
        <button
          onClick={() => navigate("/")}
          className="text-slate-400 hover:text-slate-900 p-2 -ml-2"
          data-testid="owner-login-back"
          aria-label="Zurück"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
      </header>

      <main className="flex-1 flex flex-col items-center justify-center px-6 pb-12">
        <div className="text-center mb-8">
          <img
            src="/brand/clartext-logo-full-light.svg"
            alt="CLartext"
            className="h-16 sm:h-[72px] w-auto max-w-full object-contain mx-auto mb-5"
            data-testid="app-logo-owner-login"
          />
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-900 text-white text-xs font-medium mb-4">
            <Briefcase className="w-3.5 h-3.5" />
            Unternehmer
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900">
            Master-PIN
          </h1>
          {hasDefaultPin ? (
            <p
              className="text-slate-500 mt-3 text-sm max-w-sm"
              data-testid="owner-login-default-hint"
            >
              Bitte geben Sie Ihre 6-stellige PIN ein. Beim ersten Login verwenden
              Sie Ihre temporäre PIN und vergeben anschließend eine eigene.
            </p>
          ) : (
            <p className="text-slate-500 mt-3 text-sm max-w-sm">
              Bitte deinen 6-stelligen Master-PIN eingeben.
            </p>
          )}
        </div>

        {/* Firmen-Code (Sprint G.2: Mandanten-Zuordnung) */}
        <div className="w-full max-w-xs mb-7">
          <label
            htmlFor="owner-company-code"
            className="block text-xs font-medium text-slate-500 mb-1.5 text-center uppercase tracking-wider"
          >
            Firmen-Code
          </label>
          <input
            id="owner-company-code"
            data-testid="owner-company-code-input"
            value={companyCode}
            onChange={(e) =>
              setCompanyCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))
            }
            placeholder="z. B. LUMPE"
            autoCapitalize="characters"
            autoComplete="off"
            maxLength={16}
            className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 bg-white text-slate-900 text-center text-lg tracking-[0.2em] font-semibold focus:border-slate-900 focus:outline-none transition-colors"
          />
        </div>

        {/* PIN-Dots */}
        <div
          className={`flex items-center gap-3 mb-12 ${shake ? "animate-shake-light" : ""}`}
          data-testid="owner-pin-display"
        >
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div
              key={i}
              className={`w-4 h-4 rounded-full border-2 transition-all ${
                pin.length > i
                  ? "bg-slate-900 border-slate-900"
                  : "border-slate-300 bg-transparent"
              }`}
              data-testid={`owner-pin-dot-${i}`}
            />
          ))}
        </div>

        {/* Tastenfeld */}
        <div
          className="grid grid-cols-3 gap-3 w-full max-w-xs"
          data-testid="owner-pin-keypad"
        >
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
            <KeyButton
              key={n}
              onClick={() => append(String(n))}
              disabled={submitting}
              testId={`owner-pin-key-${n}`}
            >
              {n}
            </KeyButton>
          ))}
          <div />
          <KeyButton
            onClick={() => append("0")}
            disabled={submitting}
            testId="owner-pin-key-0"
          >
            0
          </KeyButton>
          <KeyButton
            onClick={backspace}
            disabled={submitting || pin.length === 0}
            testId="owner-pin-key-backspace"
            variant="ghost"
          >
            <Delete className="w-6 h-6" />
          </KeyButton>
        </div>

        {submitting && (
          <div
            className="mt-8 flex items-center gap-2 text-slate-500 text-sm"
            data-testid="owner-pin-submitting"
          >
            <Loader2 className="w-4 h-4 animate-spin" />
            Anmeldung läuft…
          </div>
        )}
      </main>

      {/* ABO-RECHT-02, Betreiberfreigabe 27.10.2026 — Rechtslinks klein unter dem Anmeldeformular. */}
      <LegalFooter className="px-6 pb-6" />

      <style>{`
        @keyframes shake-light {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-6px); }
          50% { transform: translateX(6px); }
          75% { transform: translateX(-3px); }
        }
        .animate-shake-light { animation: shake-light 0.4s ease-in-out; }
      `}</style>
    </div>
  );
}

function KeyButton({ children, onClick, disabled, testId, variant }) {
  const base =
    "h-16 sm:h-20 rounded-full font-heading text-2xl sm:text-3xl font-light transition-all active:scale-95 select-none flex items-center justify-center";
  const colors =
    variant === "ghost"
      ? "bg-transparent text-slate-500 hover:bg-slate-100"
      : "bg-white text-slate-900 border border-slate-200 hover:border-slate-400 shadow-sm";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={`${base} ${colors} disabled:opacity-40`}
    >
      {children}
    </button>
  );
}
